import type { IssueKind, State, Status } from '@/model/sourceState/sourcestate.generated';
import type {
  SourcePhase,
  SourceStatusView,
  StatusTone,
} from '@/viewmodel/features/sourceState/types';

const STATUS_VIEW: Record<Status, { tone: StatusTone; labelKey: string }> = {
  pending_setup: { tone: 'neutral', labelKey: 'sourceState.status.pendingSetup' },
  syncing: { tone: 'info', labelKey: 'sourceState.status.syncing' },
  ready: { tone: 'success', labelKey: 'sourceState.status.ready' },
  attention: { tone: 'warning', labelKey: 'sourceState.status.attention' },
  failed: { tone: 'danger', labelKey: 'sourceState.status.failed' },
  disabled: { tone: 'neutral', labelKey: 'sourceState.status.disabled' },
  deleting: { tone: 'neutral', labelKey: 'sourceState.status.deleting' },
  unavailable: { tone: 'neutral', labelKey: 'sourceState.status.unavailable' },
};

const UNKNOWN_STATUS: SourceStatusView = {
  status: 'unknown',
  tone: 'neutral',
  labelKey: 'sourceState.status.unknown',
  isSyncing: false,
  isFailed: false,
  isStale: false,
  isConnected: false,
};

const ACTIVITY_PHASE: Record<string, SourcePhase> = {
  bronze_layer: 'bronze',
  silver_layer: 'silver',
  gold_layer: 'silver',
};

export function toSourceStatusView(
  state: Pick<State, 'status' | 'is_stale'> | null | undefined,
): SourceStatusView {
  if (!state) return UNKNOWN_STATUS;
  const known = STATUS_VIEW[state.status];
  if (!known) return UNKNOWN_STATUS;

  return {
    status: state.status,
    tone: known.tone,
    labelKey: known.labelKey,
    isSyncing: state.status === 'syncing',
    isFailed: state.status === 'failed',
    isStale: state.is_stale,
    isConnected: state.status === 'ready' || state.status === 'attention',
  };
}

export function toSourcePhase(
  status: Status | undefined,
  issueKind: IssueKind | undefined,
  activityLayer: string | undefined,
): SourcePhase {
  switch (status) {
    case 'failed':
      return 'failed';
    case 'attention':
      return issueKind === 'source_failed' ? 'stale' : 'success';
    case 'ready':
      return 'success';
    case 'syncing':
      return (activityLayer && ACTIVITY_PHASE[activityLayer]) || 'queued';
    default:
      return 'idle';
  }
}
